import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { http } from '@/lib/http';
import { useInfiniteList } from '@/lib/pagination/use-infinite-list';
import type { DisruptionPnrGroupPage, DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// Case-scoped PNR-group roster — the data behind the case-detail operation
// center (the same `RosterPnrDrawer`/`components/pnr/*` family the disruption
// control center uses). The BFF serves the SAME `DisruptionPnrGroupRow` shape
// from `GET /v1/cases/:caseUrn/pnr-groups` (the case-narrowed projection), so the
// drawer + roster row presentation are reused verbatim at case scope.

export interface CasePnrGroupsFilters {
  /** Free-text needle — PNR / passenger name / flight (server-side). */
  q?: string;
}

export async function fetchCasePnrGroupsPage(
  caseUrn: string,
  args: CasePnrGroupsFilters & { cursor?: string | undefined },
): Promise<DisruptionPnrGroupPage> {
  const params = new URLSearchParams();
  if (args.q) params.set('q', args.q);
  if (args.cursor) params.set('cursor', args.cursor);
  const qs = params.toString();
  return http
    .get(`v1/cases/${encodeURIComponent(caseUrn)}/pnr-groups${qs ? `?${qs}` : ''}`)
    .json<DisruptionPnrGroupPage>();
}

/**
 * Cursor-paginated case PNR-group roster. Mirrors `useDisruptionRoster` but keyed
 * on the case (`['cases', caseUrn, 'pnr-groups']`) — the cache slice the
 * operation-center drawer mutations invalidate at case scope (see
 * `lib/pnr/scope.ts`). One case is bounded (one flight's PNRs), but the infinite
 * list keeps the contract identical to the disruption roster.
 */
/**
 * Tenant-wide passenger/PNR search over the recent window (dashboard lookup).
 * `GET /v1/cases/passengers/search?q=` returns the SAME `DisruptionPnrGroupRow`
 * rows the rosters render, so a hit opens the full PNR operation center in
 * place. Enabled from 2 characters; previous results stay while typing.
 */
export function usePnrSearch(q: string) {
  const needle = q.trim();
  return useQuery({
    queryKey: ['cases', 'pnr-search', needle],
    queryFn: () =>
      http
        .get(`v1/cases/passengers/search?q=${encodeURIComponent(needle)}`)
        .json<DisruptionPnrGroupPage>(),
    enabled: needle.length >= 2,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });
}

export function useCasePnrGroups(
  caseUrn: string | undefined,
  filters: CasePnrGroupsFilters = {},
  enabled = true,
) {
  return useInfiniteList<DisruptionPnrGroupRow, CasePnrGroupsFilters>({
    baseKey: ['cases', caseUrn, 'pnr-groups'],
    filters,
    queryFn: (f, cursor) =>
      fetchCasePnrGroupsPage(caseUrn!, { ...f, ...(cursor ? { cursor } : {}) }),
    staleTime: 15_000,
    enabled: enabled && !!caseUrn,
  });
}
